import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectMemberProfile } from '@/state/member/memberSelectors';
import { ZOPIM_CHAT_READY_EVENT } from '@/hooks/useZopimChat';

const ZOPIM_SCRIPT_SRC = 'https://v2.zopim.com/?4tkZ0MDJSiHpTNj96D7LECpXkuprmxtc';

const ZopimChat: React.FC = () => {
    const [renderChat, setRenderChat] = useState<boolean>();
    const [isScriptReady, setIsScriptReady] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const member = useSelector((state: RootState) => selectMemberProfile(state));
    const didAttachLoadListener = useRef(false);

    useEffect(() => {
        // iOS Safari may not implement requestIdleCallback.
        if (typeof window.requestIdleCallback === 'function') {
            const idleId = window.requestIdleCallback(() => setRenderChat(true), {
                timeout: 5000,
            });
            return () => window.cancelIdleCallback?.(idleId);
        }

        const timeoutId = window.setTimeout(() => setRenderChat(true), 3000);
        return () => window.clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (!member || !renderChat || typeof window === 'undefined') return;

        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src="${ZOPIM_SCRIPT_SRC}"]`,
        );

        const markReady = () => {
            setIsScriptReady(true);
            window.dispatchEvent(new Event(ZOPIM_CHAT_READY_EVENT));
        };

        if (window.$zopim) {
            markReady();
            return;
        }

        if (existingScript) {
            if (existingScript.dataset.loaded === 'true') {
                markReady();
                return;
            }

            if (!didAttachLoadListener.current) {
                existingScript.addEventListener('load', markReady, { once: true });
                didAttachLoadListener.current = true;
            }
            return;
        }

        const script = document.createElement('script');
        script.src = ZOPIM_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.charset = 'utf-8';
        script.addEventListener(
            'load',
            () => {
                script.dataset.loaded = 'true';
                markReady();
            },
            { once: true },
        );
        document.head.appendChild(script);
    }, [member, renderChat]);

    useEffect(() => {
        if (!member || !renderChat || !isScriptReady || !window.$zopim) return;

        window.$zopim(() => {
            window.$zopim?.livechat?.set({
                name: member.username,
                email: member.email,
            });
        });
    }, [isScriptReady, member, renderChat]);

    useEffect(() => {
        if (!member || !renderChat || !isScriptReady) return;
    }, [isMobile, isScriptReady, member, renderChat]);

    return null;
};

export default ZopimChat;
